// src/components/AnalyticsPanel.js

import { useMemo } from 'react';
import { TrendingUp, AlertTriangle, Package, Truck, Target, Activity } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { useTheme } from '../context/ThemeContext';

function MiniStatCard({ label, value, color, colors }) {
  return (
    <div style={{ textAlign: 'center', padding: '0.75rem' }}>
      <div style={{
        fontSize: '1.5rem',
        fontWeight: 700,
        color: color,
        marginBottom: '0.25rem',
        fontFeatureSettings: '"tnum"',
      }}>
        {value}
      </div>
      <div style={{ fontSize: '0.65rem', color: colors.textMuted, fontWeight: 500 }}>
        {label}
      </div>
    </div>
  );
}

function RiskShipmentCard({ shipment, riskScore, riskFactors, colors, onClick }) {
  const riskColor = riskScore >= 70 ? colors.danger : riskScore >= 40 ? colors.warning : colors.success;
  const riskLevel = riskScore >= 70 ? 'HIGH' : riskScore >= 40 ? 'MED' : 'LOW';

  return (
    <div
      onClick={onClick}
      style={{
        background: colors.bgElevated,
        borderRadius: '10px',
        padding: '0.85rem',
        marginBottom: '0.6rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderLeft: `3px solid ${riskColor}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = colors.tableRowHover;
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = colors.bgElevated;
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginBottom: '0.15rem', fontFeatureSettings: '"tnum"' }}>
            {shipment.id}
          </div>
          <div style={{ fontSize: '0.8rem', color: colors.textPrimary, fontWeight: 600 }}>
            {shipment.productName?.split(' ')[0] || 'Unknown'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: riskColor, fontFeatureSettings: '"tnum"' }}>
            {riskScore.toFixed(0)}%
          </div>
          <div style={{
            fontSize: '0.55rem',
            fontWeight: 700,
            color: riskColor,
            background: `${riskColor}20`,
            padding: '0.1rem 0.3rem',
            borderRadius: '3px',
          }}>
            {riskLevel} RISK
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
        {riskFactors.slice(0, 2).map((factor, i) => (
          <span key={i} style={{
            fontSize: '0.6rem',
            color: colors.textMuted,
            background: colors.bgCard,
            padding: '0.15rem 0.35rem',
            borderRadius: '3px',
          }}>
            {factor}
          </span>
        ))}
      </div>
    </div>
  );
}

function AnalyticsPanel({ shipments, excursions, onSelectShipment }) {
  const { colors } = useTheme();

  // Risk calculations
  const atRiskShipments = useMemo(() => {
    const inTransit = shipments.filter(s => s.status === 'In Transit');
    
    return inTransit.map(s => {
      let riskScore = 0;
      const riskFactors = [];

      // Carrier risk
      const carrierExcursions = shipments.filter(sh => sh.carrierName === s.carrierName && sh.hasExcursion).length;
      const carrierTotal = shipments.filter(sh => sh.carrierName === s.carrierName).length;
      const carrierRisk = (carrierExcursions / carrierTotal) * 100;
      riskScore += carrierRisk * 0.4;
      if (carrierRisk > 35) riskFactors.push(`High-risk carrier`);

      // Product risk
      const productExcursions = shipments.filter(sh => sh.productType === s.productType && sh.hasExcursion).length;
      const productTotal = shipments.filter(sh => sh.productType === s.productType).length;
      const productRisk = (productExcursions / productTotal) * 100;
      riskScore += productRisk * 0.3;
      if (productRisk > 35) riskFactors.push(`Sensitive product`);

      // Transit time risk
      const transitRisk = Math.min(((s.transitHours || 0) / 72) * 50, 50);
      riskScore += transitRisk * 0.2;
      if ((s.transitHours || 0) > 48) riskFactors.push(`Long transit (${s.transitHours}h)`);

      // Temperature range risk
      const tempRange = (s.tempMax || 8) - (s.tempMin || 2);
      const tempRisk = tempRange < 10 ? 40 : 10;
      riskScore += tempRisk * 0.1;
      if (tempRange < 10) riskFactors.push(`Narrow temp range`);

      return { ...s, riskScore: Math.min(riskScore, 100), riskFactors };
    }).sort((a, b) => b.riskScore - a.riskScore);
  }, [shipments]);

  // Stats
  const riskStats = useMemo(() => {
    const highRisk = atRiskShipments.filter(s => s.riskScore >= 70).length;
    const mediumRisk = atRiskShipments.filter(s => s.riskScore >= 40 && s.riskScore < 70).length;
    const lowRisk = atRiskShipments.filter(s => s.riskScore < 40).length;
    const avgRisk = atRiskShipments.length > 0 
      ? atRiskShipments.reduce((sum, s) => sum + s.riskScore, 0) / atRiskShipments.length 
      : 0;
    return { highRisk, mediumRisk, lowRisk, avgRisk };
  }, [atRiskShipments]);

  // Radar data
  const radarData = useMemo(() => {
    const carrierRisks = {};
    shipments.forEach(s => {
      if (!carrierRisks[s.carrierName]) carrierRisks[s.carrierName] = { total: 0, excursions: 0 };
      carrierRisks[s.carrierName].total++;
      if (s.hasExcursion) carrierRisks[s.carrierName].excursions++;
    });
    
    const avgCarrierRisk = Object.values(carrierRisks).reduce((sum, c) => sum + (c.excursions / c.total) * 100, 0) / Object.keys(carrierRisks).length;
    const avgTransitHours = shipments.reduce((sum, s) => sum + (s.transitHours || 0), 0) / shipments.length;
    const criticalRate = (excursions.filter(e => e.severity === 'Critical').length / (excursions.length || 1)) * 100;
    
    return [
      { factor: 'Carrier Risk', value: Math.min(avgCarrierRisk, 50), fullMark: 50 },
      { factor: 'Product Risk', value: 30, fullMark: 50 },
      { factor: 'Transit Time', value: Math.min((avgTransitHours / 72) * 50, 50), fullMark: 50 },
      { factor: 'Critical Rate', value: Math.min(criticalRate, 50), fullMark: 50 },
      { factor: 'Volume', value: Math.min(shipments.length / 20, 50), fullMark: 50 },
    ];
  }, [shipments, excursions]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Risk Stats */}
      <div style={{
        background: colors.bgCard,
        backgroundImage: colors.gradientCard,
        borderRadius: '12px',
        boxShadow: colors.cardShadow,
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '0',
        }}>
          <MiniStatCard label="High Risk" value={riskStats.highRisk} color={colors.danger} colors={colors} />
          <MiniStatCard label="Medium Risk" value={riskStats.mediumRisk} color={colors.warning} colors={colors} />
          <MiniStatCard label="Low Risk" value={riskStats.lowRisk} color={colors.success} colors={colors} />
          <MiniStatCard label="Avg Risk" value={`${riskStats.avgRisk.toFixed(0)}%`} color={colors.primary} colors={colors} />
        </div>
      </div>

      {/* At-Risk Shipments */}
      <div style={{
        background: colors.bgCard,
        backgroundImage: colors.gradientCard,
        borderRadius: '12px',
        boxShadow: colors.cardShadow,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '0.85rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: colors.bgSecondary,
        }}>
          <AlertTriangle size={16} color={colors.danger} />
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary }}>
            At-Risk In-Transit
          </span>
        </div>
        <div style={{ padding: '0.75rem', maxHeight: '280px', overflowY: 'auto' }}>
          {atRiskShipments.slice(0, 4).map(s => (
            <RiskShipmentCard
              key={s.id}
              shipment={s}
              riskScore={s.riskScore}
              riskFactors={s.riskFactors}
              colors={colors}
              onClick={() => onSelectShipment && onSelectShipment(s)}
            />
          ))}
          {atRiskShipments.length === 0 && (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: colors.textMuted, fontSize: '0.8rem' }}>
              No in-transit shipments
            </div>
          )}
        </div>
      </div>

      {/* Risk Profile Radar */}
      <div style={{
        background: colors.bgCard,
        backgroundImage: colors.gradientCard,
        borderRadius: '12px',
        boxShadow: colors.cardShadow,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '0.85rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: colors.bgSecondary,
        }}>
          <Target size={16} color={colors.primary} />
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary }}>
            Overall Risk Profile
          </span>
        </div>
        <div style={{ padding: '0.5rem', height: '200px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
              <PolarGrid stroke={colors.bgElevated} />
              <PolarAngleAxis 
                dataKey="factor" 
                tick={{ fontSize: 9, fill: colors.textMuted }} 
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 50]} 
                tick={{ fontSize: 8, fill: colors.textMuted }}
                axisLine={false}
              />
              <Radar
                name="Risk"
                dataKey="value"
                stroke={colors.primary}
                fill={colors.primary}
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Scatter placeholder for Risk vs Transit */}
      <div style={{
        background: colors.bgCard,
        backgroundImage: colors.gradientCard,
        borderRadius: '12px',
        boxShadow: colors.cardShadow,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '0.85rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: colors.bgSecondary,
        }}>
          <Activity size={16} color={colors.info} />
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary }}>
            Risk Score vs Transit Time
          </span>
        </div>
        <div style={{ padding: '1rem', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {atRiskShipments.slice(0, 12).map((s, i) => {
              const riskColor = s.riskScore >= 70 ? colors.danger : s.riskScore >= 40 ? colors.warning : colors.success;
              return (
                <div
                  key={i}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: riskColor,
                    boxShadow: `0 0 8px ${riskColor}60`,
                  }}
                  title={`${s.id}: ${s.riskScore.toFixed(0)}% risk`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsPanel;